"use client";

import { c as _c } from "react/compiler-runtime";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { HiOutlineBeaker } from "react-icons/hi";
import AddDoseWrapper from "@/components/dose/add-dose-wrapper";
import { DETAILED_DOSE_TABLE_COLUMN_COUNT, DetailedDoseTableHead, DetailedDoseTableRow } from "@/components/dose/detailed-dose-table";
import { DoseLogTable } from "@/components/dose/dose-log-table/dose-log-table";
import { DoseStatusBadge as DoseManagementStatusBadge } from "@/components/dose/dose-status-badge";
import SignOffDoseModal from "@/components/dose/sign-off-dose-modal";
import Pagination from "@/components/pagination";
import { Button } from "@/components/ui";
import { GroupByDropdown, GroupedTableBody, Table, TableBody } from "@/components/ui/table";
import { useToast } from "@/contexts/toaster";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useDoseLogTable } from "@/hooks/use-dose-log-table";
import { getDoseComponentDilutionLabel } from "@/lib/dilution";
import { type MedicationDayTotal, getDoseComponentName, getDoseComponentSummary, getDoseComponents, layoutDoseRowsByMedication } from "@/lib/dose-components";
import { formatDate, formatName, formatStaffName, formatToHumanReadable, formatUnit } from "@/lib/format-data";
import { clickableText } from "@/lib/tailwind-utils";
import { MedicationSummary } from "@/types/medication";
import { NoteDetail, NoteListItem } from "@/types/notes";
import { PatientDetail } from "@/types/patient";
import { useSignOffMedicationMutation } from "@/api/provider/user/patient/patient-mutation";
const DOSES_PER_PAGE = 10;
interface DoseTableProps {
  patient: PatientDetail;
  selectedNote?: NoteListItem | NoteDetail | null;
  defaultGroupId?: string | null;
  showAddButton?: boolean;
  autoCollapseOlder?: boolean;
  title?: string;
  subtitle?: string;
}
export interface DoseTableRef {
  exportToCSV: () => void;
}

// Effective status for grouping. The `status` column is authoritative when set,
// but is null for most legacy/existing meds — so fall back to parsing the
// webhook-generated comment log ("Prescription inactive: ..." / "Prescription
// deleted: ...", see decoda/apps/api/webhooks/dosespot.py). Precedence
// deleted > inactive mirrors the webhook's own precedence. Grouping-only: the
// status badge deliberately does NOT use this inference (a stale comment can
// linger after an inactive→active transition, which is acceptable for a group
// header but not a badge).
export type EffectiveStatus = "active" | "inactive" | "deleted";
export function getEffectiveStatus(m: MedicationSummary): EffectiveStatus {
  if (m.status) return m.status;
  const c = m.comment ?? "";
  // Anchor to the start of a line (multiline flag): webhook markers are prepended
  // per accumulated log line, so match those exactly rather than a loose substring
  // — a free-text comment that merely mentions the phrase mid-line must not group.
  if (/^prescription deleted/im.test(c)) return "deleted";
  if (/^prescription inactive/im.test(c)) return "inactive";
  return "active"; // includes true "no status" meds
}
export const DoseTable = forwardRef<DoseTableRef, DoseTableProps>(function DoseTable(t0, ref) {
  const $ = _c(99);
  const {
    patient,
    selectedNote,
    defaultGroupId: t1,
    showAddButton: t2,
    autoCollapseOlder: t3,
    title,
    subtitle
  } = t0;
  const defaultGroupId = t1 === undefined ? null : t1;
  const showAddButton = t2 === undefined ? false : t2;
  const autoCollapseOlder = t3 === undefined ? false : t3;
  const {
    toast
  } = useToast();
  const {
    currentUser
  } = useUser();
  const {
    isOn: showsDoseLogTable
  } = useDoseLogTable();
  const [showOlderMedications, setShowOlderMedications] = useState(false);
  const [page, setPage] = useState(1);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const [pageByGroup, setPageByGroup] = useState(t4);
  const [pendingSignOffMedication, setPendingSignOffMedication] = useState(null);
  const signOffMedicationMutation = useSignOffMedicationMutation();
  let t5;
  if ($[1] !== patient.medications) {
    t5 = patient.medications?.filter(_temp) || [];
    $[1] = patient.medications;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const medications = t5;
  const [seenDoseCount, setSeenDoseCount] = useState(medications.length);
  if (medications.length !== seenDoseCount) {
    if (medications.length > seenDoseCount) {
      setPage(1);
      setPageByGroup({});
    }
    setSeenDoseCount(medications.length);
  }
  const sortByDate = _temp2;
  const clock = useClinicClock();
  let date;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    date = new Date();
    date.setDate(date.getDate() - 60);
    $[3] = date;
  } else {
    date = $[3];
  }
  const cutoffDate = date;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = date_0 => {
      if (!date_0) {
        return false;
      }
      return new Date(date_0) >= cutoffDate;
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const isRecent = t6;
  let t7;
  bb0: {
    if (autoCollapseOlder) {
      let t8;
      let t9;
      if ($[5] !== medications) {
        let t10;
        if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = m => isRecent(m.doseDate || m.createdDate);
          $[8] = t10;
        } else {
          t10 = $[8];
        }
        const recent = medications.filter(t10);
        let t11;
        if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = m_0 => !isRecent(m_0.doseDate || m_0.createdDate);
          $[9] = t11;
        } else {
          t11 = $[9];
        }
        const older = medications.filter(t11);
        t8 = sortByDate(recent, false);
        t9 = sortByDate(older, false);
        $[5] = medications;
        $[6] = t8;
        $[7] = t9;
      } else {
        t8 = $[6];
        t9 = $[7];
      }
      let t10;
      if ($[10] !== t8 || $[11] !== t9) {
        t10 = {
          recentMedications: t8,
          olderMedications: t9
        };
        $[10] = t8;
        $[11] = t9;
        $[12] = t10;
      } else {
        t10 = $[12];
      }
      t7 = t10;
      break bb0;
    }
    let t8;
    if ($[13] !== medications) {
      t8 = sortByDate(medications, false);
      $[13] = medications;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = [];
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] !== t8) {
      t10 = {
        recentMedications: t8,
        olderMedications: t9
      };
      $[16] = t8;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    t7 = t10;
  }
  const {
    recentMedications,
    olderMedications
  } = t7;
  let t8;
  bb1: {
    if (autoCollapseOlder && !showOlderMedications && recentMedications.length > 0) {
      t8 = recentMedications;
      break bb1;
    }
    let t9;
    if ($[18] !== olderMedications || $[19] !== recentMedications) {
      t9 = [...recentMedications, ...olderMedications];
      $[18] = olderMedications;
      $[19] = recentMedications;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t8 = t9;
  }
  const displayedMedications = t8;
  const totalPages = Math.max(1, Math.ceil(displayedMedications.length / DOSES_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  let t9;
  if ($[21] !== currentPage || $[22] !== displayedMedications) {
    t9 = displayedMedications.slice((currentPage - 1) * DOSES_PER_PAGE, currentPage * DOSES_PER_PAGE);
    $[21] = currentPage;
    $[22] = displayedMedications;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const pagedMedications = t9;
  let t10;
  if ($[24] !== clock || $[25] !== displayedMedications || $[26] !== pagedMedications) {
    t10 = layoutDoseRowsByMedication(pagedMedications, clock, displayedMedications);
    $[24] = clock;
    $[25] = displayedMedications;
    $[26] = pagedMedications;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const pagedLayout = t10;
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      id: "date",
      label: "Created Date",
      paramKey: "createdDate" as const
    };
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      id: "medication",
      label: "Components",
      paramKey: "medicationName" as const
    };
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      id: "provider",
      label: "Provider",
      paramKey: "providerId" as const
    };
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [t11, t12, t13, {
      id: "status",
      label: "Prescription status",
      paramKey: "status" as const
    }];
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const groupOptions = t14 as const;
  const [groupById, setGroupById] = useState(defaultGroupId);
  let t15;
  if ($[32] !== groupById) {
    t15 = groupById ? groupOptions.find(o => o.id === groupById) ?? null : null;
    $[32] = groupById;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const selectedGroup = t15;
  let t16;
  if ($[34] !== currentUser?.id || $[35] !== patient.id || $[36] !== pendingSignOffMedication || $[37] !== signOffMedicationMutation) {
    t16 = async () => {
      if (!pendingSignOffMedication) {
        return;
      }
      await signOffMedicationMutation.mutateAsync({
        medicationId: pendingSignOffMedication.id,
        patientId: patient.id,
        currentProviderId: currentUser?.id
      });
      setPendingSignOffMedication(null);
    };
    $[34] = currentUser?.id;
    $[35] = patient.id;
    $[36] = pendingSignOffMedication;
    $[37] = signOffMedicationMutation;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  currentUser?.id;
  const handleSignOffConfirm = t16;
  let t17;
  if ($[39] !== clock || $[40] !== medications || $[41] !== patient.firstName || $[42] !== patient.lastName || $[43] !== toast) {
    t17 = () => {
      const sortedMedications = sortByDate(medications, false);
      const headers = ["Dose ID", "Patient Name", "Dose Components", "Amount", "Unit", "Quantity", "Dose Date", "Status", "Provider", "Created By", "Location", "Frequency", "Reconstitution", "Side Effects", "Lot Number", "Comment", "Created Date", "Signed Off Provider Id", "Signed Off Date"];
      const rows = sortedMedications.map(med_0 => {
        const components = getDoseComponents(med_0);
        return [med_0.id, formatName(patient.firstName, patient.lastName), components.map(getDoseComponentName).join(" + "), components.map(_temp3).join(" + "), components.map(_temp4).join(" + "), components.map(_temp5).join(" + "), med_0.doseDate ? formatDate(med_0.doseDate, true, true) : "", med_0.managementStatus ? formatToHumanReadable(med_0.managementStatus) : "", med_0.provider ? formatStaffName(med_0.provider) : "", med_0.createdBy ? formatStaffName(med_0.createdBy) : "", med_0.administrationLocation || "", med_0.frequency || "", components.map(getDoseComponentDilutionLabel).filter(Boolean).join(" + "), med_0.sideEffects || "", components.flatMap(_temp6).map(_temp7).filter(Boolean).join(" + "), med_0.comment || "", formatDate(med_0.createdDate, true, true), med_0.signedOffProviderId || "", med_0.signedOffDate ? formatDate(med_0.signedOffDate, true, true) : ""];
      });
      const csvContent = [headers.join(","), ...rows.map(_temp9)].join("\n");
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;"
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const patientName = formatName(patient.firstName, patient.lastName).replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
      const fileName = `dose_log_${patientName}_${clock.today()}.csv`;
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({
        title: "Export Successful",
        description: "Dose log has been exported to CSV",
        variant: "success"
      });
    };
    $[39] = clock;
    $[40] = medications;
    $[41] = patient.firstName;
    $[42] = patient.lastName;
    $[43] = toast;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const handleExportToCSV = t17;
  let t18;
  if ($[45] !== handleExportToCSV) {
    t18 = () => ({
      exportToCSV: handleExportToCSV
    });
    $[45] = handleExportToCSV;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  useImperativeHandle(ref, t18);
  let t19;
  if ($[47] !== clock) {
    t19 = m_1 => {
      const day = clock.dayOf(m_1.createdDate);
      const today = clock.today();
      if (day === today) {
        return "today";
      }
      if (day < today) {
        return "past";
      }
      return "other";
    };
    $[47] = clock;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const getDateGroupKey = t19;
  let t20;
  if ($[49] !== displayedMedications || $[50] !== getDateGroupKey) {
    t20 = paramKey => {
      if (paramKey === "createdDate") {
        const todayMeds = displayedMedications.filter(m_2 => getDateGroupKey(m_2) === "today");
        const pastMeds = displayedMedications.filter(m_3 => getDateGroupKey(m_3) === "past");
        return [{
          key: "today",
          label: "Doses Created Today",
          total: todayMeds.length,
          rows: sortByDate(todayMeds, false)
        }, {
          key: "past",
          label: "Doses Created in the Past",
          total: pastMeds.length,
          rows: sortByDate(pastMeds, false)
        }];
      }
      if (paramKey === "medicationName") {
        const map = new Map();
        displayedMedications.forEach(m_4 => {
          const k = getDoseComponentSummary(m_4);
          if (!map.has(k)) {
            map.set(k, []);
          }
          map.get(k).push(m_4);
        });
        return Array.from(map.entries()).map(t21 => {
          const [key, meds_0] = t21;
          return {
            key,
            label: key,
            total: meds_0.length,
            rows: sortByDate(meds_0, false)
          };
        });
      }
      if (paramKey === "status") {
        const order = [{
          key: "active",
          label: "Active"
        }, {
          key: "inactive",
          label: "Inactive"
        }, {
          key: "deleted",
          label: "Deleted"
        }];
        return order.map(t22 => {
          const {
            key: key_0,
            label
          } = t22;
          const meds_1 = displayedMedications.filter(m_5 => getEffectiveStatus(m_5) === key_0);
          return {
            key: key_0,
            label,
            total: meds_1.length,
            rows: sortByDate(meds_1, false)
          };
        }).filter(_temp10);
      }
      const map_0 = new Map();
      displayedMedications.forEach(m_6 => {
        const k_0 = m_6.provider?.id || "unknown";
        const label_0 = m_6.provider ? formatStaffName(m_6.provider) : "Unknown Provider";
        if (!map_0.has(k_0)) {
          map_0.set(k_0, {
            label: label_0,
            meds: []
          });
        }
        map_0.get(k_0).meds.push(m_6);
      });
      return Array.from(map_0.entries()).map(t23 => {
        const [key_1, v] = t23;
        return {
          key: key_1,
          label: v.label,
          total: v.meds.length,
          rows: sortByDate(v.meds, false)
        };
      });
    };
    $[49] = displayedMedications;
    $[50] = getDateGroupKey;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  const buildGroups = t20;
  let t21;
  bb2: {
    if (!selectedGroup) {
      t21 = null;
      break bb2;
    }
    let t22;
    if ($[52] !== buildGroups || $[53] !== clock || $[54] !== pageByGroup || $[55] !== selectedGroup.paramKey) {
      const groups = buildGroups(selectedGroup.paramKey);
      const clampedPageByGroup = {};
      const rowsByGroup = {};
      const summaryBefore = new Map();
      groups.forEach(g_0 => {
        const groupPages = Math.max(1, Math.ceil(g_0.total / DOSES_PER_PAGE));
        const groupPage = Math.min(pageByGroup[g_0.key] ?? 1, groupPages);
        clampedPageByGroup[g_0.key] = groupPage;
        const layout = layoutDoseRowsByMedication(g_0.rows.slice((groupPage - 1) * DOSES_PER_PAGE, groupPage * DOSES_PER_PAGE), clock, g_0.rows);
        rowsByGroup[g_0.key] = layout.rows;
        layout.headerBefore.forEach((total, id) => summaryBefore.set(id, total));
      });
      t22 = {
        groups,
        rowsByGroup,
        pageByGroup: clampedPageByGroup,
        summaryBefore
      };
      $[52] = buildGroups;
      $[53] = clock;
      $[54] = pageByGroup;
      $[55] = selectedGroup.paramKey;
      $[56] = t22;
    } else {
      t22 = $[56];
    }
    t21 = t22;
  }
  const groupedData = t21;
  let t22;
  if ($[57] !== groupedData?.pageByGroup) {
    t22 = (key_2, delta) => {
      setPageByGroup(prev => ({
        ...prev,
        [key_2]: (groupedData?.pageByGroup[key_2] ?? 1) + delta
      }));
    };
    $[57] = groupedData?.pageByGroup;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  const changeGroupPage = t22;
  let t23;
  if ($[59] !== patient || $[60] !== selectedNote) {
    t23 = (medication, summary) => <DetailedDoseTableRow key={medication.id} summary={summary} medication={medication} patient={patient} selectedNote={selectedNote} onRequestSignOff={setPendingSignOffMedication} metadata={<DoseManagementStatusBadge medication={medication} />} />;
    $[59] = patient;
    $[60] = selectedNote;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  const renderDetailedRow = t23;
  const t24 = !!pendingSignOffMedication;
  let t25;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setPendingSignOffMedication(null);
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== handleSignOffConfirm || $[64] !== patient || $[65] !== pendingSignOffMedication || $[66] !== signOffMedicationMutation.isPending || $[67] !== t24) {
    t26 = <SignOffDoseModal isOpen={t24} onClose={t25} onConfirm={handleSignOffConfirm} medication={pendingSignOffMedication} patient={patient} isLoading={signOffMedicationMutation.isPending} />;
    $[63] = handleSignOffConfirm;
    $[64] = patient;
    $[65] = pendingSignOffMedication;
    $[66] = signOffMedicationMutation.isPending;
    $[67] = t24;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  const signOffModal = t26;
  let t27;
  if ($[69] !== patient || $[70] !== showAddButton || $[71] !== subtitle || $[72] !== title) {
    t27 = (title || subtitle || showAddButton) && <div className="mb-3 flex items-start justify-between"><div>{title && <h4 className="mb-1 text-body-strong text-foreground">{title}</h4>}{subtitle && <p className="text-caption">{subtitle}</p>}</div>{showAddButton && <AddDoseWrapper patientId={patient.id} patient={patient} onSubmit={_temp11}><Button variant="secondary" size="sm" icon={FiPlus} className="ml-4">Add</Button></AddDoseWrapper>}</div>;
    $[69] = patient;
    $[70] = showAddButton;
    $[71] = subtitle;
    $[72] = title;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  const header = t27;
  if (showsDoseLogTable) {
    let t28;
    if ($[74] !== medications || $[75] !== patient || $[76] !== selectedNote) {
      t28 = <DoseLogTable patient={patient} doses={medications} selectedNote={selectedNote} />;
      $[74] = medications;
      $[75] = patient;
      $[76] = selectedNote;
      $[77] = t28;
    } else {
      t28 = $[77];
    }
    let t29;
    if ($[78] !== header || $[79] !== t28) {
      t29 = <>{header}{t28}</>;
      $[78] = header;
      $[79] = t28;
      $[80] = t29;
    } else {
      t29 = $[80];
    }
    return t29;
  }
  let t28;
  if ($[81] !== autoCollapseOlder || $[82] !== changeGroupPage || $[83] !== currentPage || $[84] !== displayedMedications.length || $[85] !== groupById || $[86] !== groupedData || $[87] !== medications.length || $[88] !== olderMedications || $[89] !== pagedLayout || $[90] !== recentMedications || $[91] !== renderDetailedRow || $[92] !== showOlderMedications || $[93] !== totalPages) {
    t28 = medications.length > 0 ? <><div className="mb-3 flex items-center justify-between"><GroupByDropdown options={groupOptions.map(_temp12)} value={groupById} onChange={id_0 => {
          setGroupById(id_0);
          setPage(1);
          setPageByGroup({});
        }} /></div><div className="w-full overflow-x-auto rounded-lg">{groupedData ? <GroupedTableBody groups={groupedData.groups} colSpan={DETAILED_DOSE_TABLE_COLUMN_COUNT} rowsByGroup={groupedData.rowsByGroup} pageByGroup={groupedData.pageByGroup} perPage={DOSES_PER_PAGE} onPrevPage={key_3 => changeGroupPage(key_3, -1)} onNextPage={key_4 => changeGroupPage(key_4, 1)} renderTableHead={_temp13} renderRow={row_0 => renderDetailedRow(row_0, groupedData.summaryBefore.get(row_0.id))} label="Doses" /> : <><Table className="w-full overflow-visible leading-normal md:table-fixed"><DetailedDoseTableHead /><TableBody>{pagedLayout.rows.map(m_7 => renderDetailedRow(m_7, pagedLayout.headerBefore.get(m_7.id)))}</TableBody></Table>{totalPages > 1 && <Pagination displayText="doses" total={displayedMedications.length} currentPage={currentPage} perPage={DOSES_PER_PAGE} handlePageChange={direction => setPage(direction === "prev" ? currentPage - 1 : currentPage + 1)} />}</>}</div>{autoCollapseOlder && olderMedications.length > 0 && recentMedications.length > 0 && <button type="button" onClick={() => {
        setPage(showOlderMedications ? 1 : Math.floor(recentMedications.length / DOSES_PER_PAGE) + 1);
        setPageByGroup({});
        setShowOlderMedications(_temp14);
      }} className={`${clickableText} mt-2`}>{showOlderMedications ? "Hide older" : `Show older (${olderMedications.length})`}</button>}</> : <div className="py-8 text-center"><HiOutlineBeaker className="mx-auto h-8 w-8 text-foreground-muted" /><p className="mt-2 text-body text-muted-foreground">No doses found</p></div>;
    $[81] = autoCollapseOlder;
    $[82] = changeGroupPage;
    $[83] = currentPage;
    $[84] = displayedMedications.length;
    $[85] = groupById;
    $[86] = groupedData;
    $[87] = medications.length;
    $[88] = olderMedications;
    $[89] = pagedLayout;
    $[90] = recentMedications;
    $[91] = renderDetailedRow;
    $[92] = showOlderMedications;
    $[93] = totalPages;
    $[94] = t28;
  } else {
    t28 = $[94];
  }
  let t29;
  if ($[95] !== header || $[96] !== signOffModal || $[97] !== t28) {
    t29 = <>{header}{t28}{signOffModal}</>;
    $[95] = header;
    $[96] = signOffModal;
    $[97] = t28;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  return t29;
});
function _temp(med) {
  return med.provider;
}
function _temp2(meds, t0) {
  const asc = t0 === undefined ? false : t0;
  return [...meds].sort((a, b) => {
    const aTime = a.doseDate ? new Date(a.doseDate).getTime() : a.createdDate ? new Date(a.createdDate).getTime() : 0;
    const bTime = b.doseDate ? new Date(b.doseDate).getTime() : b.createdDate ? new Date(b.createdDate).getTime() : 0;
    return asc ? aTime - bTime : bTime - aTime;
  });
}
function _temp3(component) {
  return component.amount ?? "";
}
function _temp4(component_0) {
  return component_0.unit ? formatUnit(component_0.unit) : "";
}
function _temp5(component_1) {
  return component_1.quantity || 1;
}
function _temp6(component_2) {
  return component_2.lots ?? [];
}
function _temp7(lot) {
  return lot.lotNumber;
}
function _temp8(cell) {
  return `"${String(cell).replace(/"/g, "\"\"")}"`;
}
function _temp9(row) {
  return row.map(_temp8).join(",");
}
function _temp10(g) {
  return g.total > 0;
}
function _temp11() {}
function _temp12(o_0) {
  return {
    id: o_0.id,
    label: o_0.label
  };
}
function _temp13() {
  return <DetailedDoseTableHead />;
}
function _temp14(visible) {
  return !visible;
}